import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type StoreCategory = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  sort_order: number;
};

export type StoreProduct = {
  id: string;
  slug: string;
  category_id: string;
  title: string;
  brand: string | null;
  short_desc: string;
  long_desc: string | null;
  image_url: string | null;
  price_display: string | null;
  affiliate_url: string;
  affiliate_network: string;
  menopause_relevance: string | null;
  is_active: boolean;
  sort_order: number;
};

const PRODUCT_COLS =
  "id, slug, category_id, title, brand, short_desc, long_desc, image_url, price_display, affiliate_url, affiliate_network, menopause_relevance, is_active, sort_order";

export function useStoreCatalog() {
  const [categories, setCategories] = useState<StoreCategory[] | null>(null);
  const [products, setProducts] = useState<StoreProduct[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: cats, error: ce }, { data: prods, error: pe }] = await Promise.all([
          supabase
            .from("store_categories")
            .select("id, slug, name, description, sort_order")
            .order("sort_order", { ascending: true }),
          supabase
            .from("store_products")
            .select(PRODUCT_COLS)
            .eq("is_active", true)
            .order("sort_order", { ascending: true }),
        ]);
        if (!alive) return;
        if (ce) throw ce;
        if (pe) throw pe;
        setCategories((cats ?? []) as StoreCategory[]);
        setProducts((prods ?? []) as StoreProduct[]);
      } catch (e) {
        if (!alive) return;
        setError(e instanceof Error ? e.message : "Failed to load catalog");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return { categories, products, error, loading: !error && (categories === null || products === null) };
}

export function useStoreProduct(slug: string) {
  const [product, setProduct] = useState<StoreProduct | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setProduct(undefined);
    setError(null);
    (async () => {
      try {
        const { data, error: e } = await supabase
          .from("store_products")
          .select(PRODUCT_COLS)
          .eq("slug", slug)
          .eq("is_active", true)
          .maybeSingle();
        if (!alive) return;
        if (e) throw e;
        setProduct((data ?? null) as StoreProduct | null);
      } catch (e) {
        if (!alive) return;
        setError(e instanceof Error ? e.message : "Failed to load product");
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  return { product, error, loading: product === undefined && !error };
}
